import { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import Instructions from './Instructions'

interface HomeProps {
  onCreateRoom: (name: string) => Promise<void>
  onJoinRoom: (code: string, name: string) => Promise<void>
  loading: boolean
}

export default function Home({ onCreateRoom, onJoinRoom, loading }: HomeProps) {
  const [mode, setMode] = useState<'home' | 'create' | 'join'>('home')
  const [name, setName] = useState('')
  const [code, setCode] = useState('')
  const [error, setError] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [howToOpen, setHowToOpen] = useState(false)

  const handleCreate = async () => {
    if (!name.trim()) return
    setSubmitting(true)
    setError('')
    try {
      await onCreateRoom(name.trim())
    } catch {
      setError('Failed to create room')
      setSubmitting(false)
    }
  }

  const handleJoin = async () => {
    if (!name.trim() || !code.trim()) return
    setSubmitting(true)
    setError('')
    try {
      await onJoinRoom(code.trim(), name.trim())
    } catch {
      setError('Room not found')
      setSubmitting(false)
    }
  }

  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ repeat: Infinity, duration: 1, ease: 'linear' }}
          className="w-16 h-16 border-4 border-coral border-t-transparent rounded-full"
        />
      </div>
    )
  }

  return (
    <div className="flex-1 flex flex-col items-center justify-start px-6 pt-12 pb-8">
      <motion.div
        initial={{ scale: 0.8, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ type: 'spring', damping: 12 }}
        className="text-center mb-8"
      >
        <h1 className="font-display text-6xl sm:text-8xl font-bold text-warmblack mb-2">
          Preferences
        </h1>
        <p className="font-body text-lg sm:text-2xl text-warmblack/60">
          Rank it. Reveal it. Argue about it.
        </p>
      </motion.div>

      {mode === 'home' && (
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.2 }}
          className="flex flex-col gap-4 w-full max-w-sm"
        >
          <button
            onClick={() => setMode('create')}
            className="bg-coral hover:bg-coral/90 text-white font-display font-bold text-2xl py-5 px-8 rounded-2xl shadow-lg hover:shadow-xl transition-all active:scale-95"
          >
            Create Room
          </button>
          <button
            onClick={() => setMode('join')}
            className="bg-white hover:bg-white/80 text-warmblack font-display font-bold text-2xl py-5 px-8 rounded-2xl shadow-lg hover:shadow-xl transition-all border-2 border-warmblack/10 active:scale-95"
          >
            Join Room
          </button>
          <button
            onClick={() => setHowToOpen(true)}
            className="text-warmblack/60 hover:text-warmblack font-body text-base underline underline-offset-2 mt-1 transition-colors"
          >
            How to play
          </button>
        </motion.div>
      )}

      {mode === 'create' && (
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          className="flex flex-col gap-4 w-full max-w-sm"
        >
          <input
            type="text"
            placeholder="Your name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={20}
            autoFocus
            className="font-body text-2xl text-center py-4 px-6 rounded-2xl border-2 border-warmblack/10 bg-white focus:border-coral focus:outline-none transition-colors"
            onKeyDown={(e) => e.key === 'Enter' && handleCreate()}
          />
          {error && <p className="text-coral text-center font-body text-lg">{error}</p>}
          <button
            onClick={handleCreate}
            disabled={!name.trim() || submitting}
            className="bg-coral hover:bg-coral/90 disabled:opacity-50 text-white font-display font-bold text-2xl py-5 px-8 rounded-2xl shadow-lg transition-all active:scale-95"
          >
            {submitting ? 'Creating...' : "Let's Go"}
          </button>
          <button
            onClick={() => { setMode('home'); setError('') }}
            className="text-warmblack/50 font-body text-lg hover:text-warmblack transition-colors"
          >
            Back
          </button>
        </motion.div>
      )}

      {mode === 'join' && (
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          className="flex flex-col gap-3 w-full max-w-sm"
        >
          <input
            type="text"
            placeholder="Room word"
            value={code}
            onChange={(e) => setCode(e.target.value.toUpperCase().replace(/[^A-Z]/g, '').slice(0, 5))}
            maxLength={5}
            autoFocus
            className="font-display text-3xl text-center tracking-[0.15em] py-4 px-6 rounded-2xl border-2 border-warmblack/10 bg-white focus:border-coral focus:outline-none transition-colors uppercase"
          />
          <input
            type="text"
            placeholder="Your name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={20}
            className="font-body text-2xl text-center py-4 px-6 rounded-2xl border-2 border-warmblack/10 bg-white focus:border-coral focus:outline-none transition-colors"
            onKeyDown={(e) => e.key === 'Enter' && handleJoin()}
          />
          {error && <p className="text-coral text-center font-body text-lg">{error}</p>}
          <button
            onClick={handleJoin}
            disabled={!name.trim() || !code.trim() || submitting}
            className="bg-coral hover:bg-coral/90 disabled:opacity-50 text-white font-display font-bold text-2xl py-5 px-8 rounded-2xl shadow-lg transition-all active:scale-95"
          >
            {submitting ? 'Joining...' : 'Join'}
          </button>
          <button
            onClick={() => { setMode('home'); setError('') }}
            className="text-warmblack/50 font-body text-lg hover:text-warmblack transition-colors"
          >
            Back
          </button>
        </motion.div>
      )}

      <AnimatePresence>
        {howToOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-warmblack/40 backdrop-blur-sm flex items-end sm:items-center justify-center px-3 pb-3 pt-12"
            onClick={() => setHowToOpen(false)}
          >
            <motion.div
              initial={{ y: 30, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 30, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-cream rounded-2xl shadow-2xl max-w-md w-full max-h-full flex flex-col"
            >
              <div className="flex items-center justify-between px-5 py-4 border-b border-warmblack/10">
                <h2 className="font-display text-2xl font-bold text-warmblack">How to play</h2>
                <button
                  aria-label="Close"
                  onClick={() => setHowToOpen(false)}
                  className="w-8 h-8 rounded-lg hover:bg-warmblack/5 text-warmblack/60 text-2xl flex items-center justify-center"
                >
                  ×
                </button>
              </div>
              <div className="flex-1 overflow-y-auto px-5 py-4">
                <Instructions />
              </div>
              <div className="px-5 py-3 border-t border-warmblack/10">
                <button
                  onClick={() => setHowToOpen(false)}
                  className="w-full bg-coral hover:bg-coral/90 text-white font-display font-bold py-3 rounded-xl transition-colors"
                >
                  Got it
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}
